import { useProctoredAttempt } from '../components/focus-guard/FocusGuardContext';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { AICore } from '../components/ai/AICore';
import { ScoreDisplay } from '../components/ai/ScoreDisplay';
import { VoiceWaveform } from '../components/ai/VoiceWaveform';
import { TTSPlayer } from '../components/ai/TTSPlayer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { useContentProtection } from '../hooks/useContentProtection';

// Simulation-specific components
import { 
    scenarioTypes, 
    jobCategories, 
    communicationTopics 
} from '../components/simulation/constants';
import { JobAnalysisPanel } from '../components/simulation/JobAnalysisPanel';
import { CommunicationPanel } from '../components/simulation/CommunicationPanel';
import { ScenarioSelector } from '../components/simulation/ScenarioSelector';
import { ScoreTrendChart } from '../components/simulation/ScoreTrendChart';
import { AssessmentPanel } from '../components/simulation/AssessmentPanel';
import { ActiveSimulationPanel } from '../components/simulation/ActiveSimulationPanel';

import { 
    Play, 
    Square, 
    Mic, 
    MicOff, 
    Send, 
    Users,
    Briefcase,
    MessageCircle,
    MessageSquare,
    DollarSign,
    RefreshCw,
    Trophy,
    Target,
    Volume2,
    VolumeX,
    Sparkles,
    ChevronRight,
    CheckCircle,
    AlertCircle,
    Zap,
    Award,
    FileText,
    Building,
    MapPin,
    GraduationCap,
    Clock,
    Loader2,
    BookOpen,
    ListChecks,
    Star,
    History,
    TrendingUp
} from 'lucide-react';


const Simulation = () => {
    const { api } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    // Check for scenario param from R-PAT Phase 4
    const scenarioFromUrl = searchParams.get('scenario');
    const fromRpat = searchParams.get('from_rpat') === 'true';
    const rpatTopicId = searchParams.get('topic');
    const rpatDifficulty = searchParams.get('difficulty'); // Get difficulty from R-PAT
    const variantFromUrl = searchParams.get('variant');
    const commTopicFromUrl = searchParams.get('comm_topic');
    const assignedTask = searchParams.get('qt');
    
    // Enable content protection (disable right-click, copy, screenshots)
    useContentProtection(true);
    
    const [simulation, setSimulation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [aiState, setAiState] = useState('idle');
    const [isRecording, setIsRecording] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingScenario, setLoadingScenario] = useState(null);
    const [scores, setScores] = useState(null);
    const [assessment, setAssessment] = useState(null);
    useProctoredAttempt({ active: Boolean(simulation) && !assessment, label: 'simulation', onForceEnd: () => completeSimulation() });
    const [audioLevel, setAudioLevel] = useState(0);
    const [autoTTS, setAutoTTS] = useState(true);
    const [selectedScenario, setSelectedScenario] = useState(null);
    const [selectedVariantId, setSelectedVariantId] = useState(null);
    const selectScenario = (scenario) => {
        setSelectedScenario(scenario);
        setSelectedVariantId(null);
    };
    
    // Job Analysis states
    const [jobAnalysisStep, setJobAnalysisStep] = useState('select'); // 'select', 'generating', 'review', 'simulation'
    const [selectedJobCategory, setSelectedJobCategory] = useState(null);
    const [generatedJob, setGeneratedJob] = useState(null);
    const [questionsAsked, setQuestionsAsked] = useState(0);
    
    // Communication Simulator states
    const [commStep, setCommStep] = useState('select'); // 'select', 'simulation'
    const [selectedCommTopic, setSelectedCommTopic] = useState(null);
    const [exchanges, setExchanges] = useState(0);
    const [realTimeTip, setRealTimeTip] = useState(null);
    const [exchangeStatus, setExchangeStatus] = useState(null);
    
    // Anti-cheating states
    const [autoRecordEnabled, setAutoRecordEnabled] = useState(true);
    const [questionStartTime, setQuestionStartTime] = useState(null);
    const [copyAttempts, setCopyAttempts] = useState(0);
    const [aiDetectionWarning, setAiDetectionWarning] = useState(null);
    const [responseMetrics, setResponseMetrics] = useState([]);
    const [ttsFinished, setTtsFinished] = useState(false);
    
    // Response timeout states (15 second limit to start answering)
    const [responseCountdown, setResponseCountdown] = useState(null);
    const [isAnswering, setIsAnswering] = useState(false);
    const RESPONSE_TIMEOUT_SECONDS = 15;
    
    const messagesEndRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const autoRecordTimeoutRef = useRef(null);
    const countdownIntervalRef = useRef(null);
    const simulationRef = useRef(null);
    const questionStartTimeRef = useRef(null);

    // Keep refs updated
    useEffect(() => {
        simulationRef.current = simulation;
    }, [simulation]);
    
    useEffect(() => {
        questionStartTimeRef.current = questionStartTime;
    }, [questionStartTime]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);
    
    // Track if we've auto-started from R-PAT
    const hasAutoStarted = useRef(false);
    
    // Auto-select scenario when coming from R-PAT Phase 4
    useEffect(() => {
        if (scenarioFromUrl && !simulation && !hasAutoStarted.current) {
            const scenario = scenarioTypes.find(s => s.id === scenarioFromUrl);
            if (scenario) {
                setSelectedScenario(scenario);
                if (variantFromUrl) setSelectedVariantId(variantFromUrl);
            }
        }
    }, [scenarioFromUrl, variantFromUrl, simulation]);

    // Quality Task link: always keep the assigned scenario selected
    useEffect(() => {
        if (!assignedTask || simulation || selectedScenario?.id === scenarioFromUrl) return;
        const scenario = scenarioTypes.find(s => s.id === scenarioFromUrl);
        if (scenario) setSelectedScenario(scenario);
    }, [assignedTask, simulation, selectedScenario, scenarioFromUrl]);
    
    // Anti-copy/paste protection during active simulation
    useEffect(() => {
        if (!simulation) return;
        
        const preventCopy = (e) => {
            if (simulation && simulation.status === 'active') {
                e.preventDefault();
                setCopyAttempts(prev => prev + 1);
                toast.warning('Copy/paste is disabled during simulation to build fluency', {
                    duration: 2000
                });
            }
        };
        
        const preventPaste = (e) => {
            if (simulation && simulation.status === 'active') {
                e.preventDefault();
                toast.warning('Paste is disabled during simulation', {
                    duration: 2000
                });
            }
        };
        
        const preventContextMenu = (e) => {
            if (simulation && simulation.status === 'active') {
                e.preventDefault();
            }
        };
        
        // Disable text selection via CSS class
        document.body.classList.add('simulation-active');
        
        document.addEventListener('copy', preventCopy);
        document.addEventListener('cut', preventCopy);
        document.addEventListener('paste', preventPaste);
        document.addEventListener('contextmenu', preventContextMenu);
        
        return () => {
            document.body.classList.remove('simulation-active');
            document.removeEventListener('copy', preventCopy);
            document.removeEventListener('cut', preventCopy);
            document.removeEventListener('paste', preventPaste);
            document.removeEventListener('contextmenu', preventContextMenu);
        };
    }, [simulation]);
    
    // Clear countdown interval on unmount
    useEffect(() => {
        return () => {
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
            }
            if (autoRecordTimeoutRef.current) {
                clearTimeout(autoRecordTimeoutRef.current);
            }
        };
    }, []);
    
    // Start the response countdown timer
    const startResponseCountdown = () => {
        // Clear any existing countdown
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
        }
        
        setResponseCountdown(RESPONSE_TIMEOUT_SECONDS);
        setIsAnswering(false);
        
        countdownIntervalRef.current = setInterval(() => {
            setResponseCountdown(prev => {
                if (prev <= 1) {
                    // Time's up! Auto-close the simulation
                    clearInterval(countdownIntervalRef.current);
                    handleTimeoutExpired();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };
    
    // Handle when user starts answering (stops the countdown)
    const handleUserStartedAnswering = () => {
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        setResponseCountdown(null);
        setIsAnswering(true);
    };
    
    // Handle timeout expiration - auto-close simulation
    const handleTimeoutExpired = async () => {
        if (!simulation) return;
        
        toast.error('Time expired! You must start answering within 15 seconds.', { duration: 5000 });
        
        try {
            // Complete the simulation (will be marked as incomplete due to low exchanges)
            const response = await api.post(`/simulation/${simulation.id}/complete`);
            setAssessment(response.data.assessment);
            if (response.data.scores) {
                setScores(response.data.scores);
            }
        } catch (error) {
            console.error('Failed to complete simulation on timeout:', error);
        }
    };
    
    // Auto-start recording when TTS finishes
    const handleTTSComplete = () => {
        setTtsFinished(true);
        setQuestionStartTime(Date.now());
        
        // Start the 15-second countdown
        startResponseCountdown();
        
        if (autoRecordEnabled && simulation && !isRecording) {
            // Small delay to let user prepare, then auto-start
            autoRecordTimeoutRef.current = setTimeout(() => {
                toast.info('Recording started automatically - speak now!', { duration: 2000 });
                startRecording();
                handleUserStartedAnswering(); // Recording started = answering started
            }, 1500); // 1.5 second grace period
        }
    };
    
    // AI-generated response detection heuristics
    const analyzeResponseForAI = (text, responseTimeMs) => {
        const warnings = [];
        const wordCount = text.split(/\s+/).length;
        const avgWordLength = text.replace(/\s+/g, '').length / wordCount;
        
        // Very fast, long responses are suspicious
        const wordsPerSecond = wordCount / (responseTimeMs / 1000);
        if (wordsPerSecond > 3 && wordCount > 30) {
            warnings.push('Response delivered faster than typical speech pace');
        }
        
        // AI patterns: overly formal, list-like structures
        const aiPatterns = [
            /firstly|secondly|thirdly|furthermore|moreover|additionally/gi,
            /in conclusion|to summarize|in summary|to conclude/gi,
            /it is worth noting|it should be noted|it is important to/gi,
            /\d+\.\s+\w+.*\d+\.\s+\w+/s, // numbered lists
        ];
        
        let patternMatches = 0;
        aiPatterns.forEach(pattern => {
            if (pattern.test(text)) patternMatches++;
        });
        
        if (patternMatches >= 2) {
            warnings.push('Response contains patterns common in AI-generated text');
        }
        
        // Unusually consistent, formal vocabulary
        const formalWords = ['therefore', 'consequently', 'nevertheless', 'notwithstanding', 'aforementioned', 'subsequently'];
        const formalCount = formalWords.filter(w => text.toLowerCase().includes(w)).length;
        if (formalCount >= 2) {
            warnings.push('Unusually formal vocabulary detected');
        }
        
        return warnings;
    };

    // Generate a random job description
    const generateJob = async (category) => {
        setSelectedJobCategory(category);
        setJobAnalysisStep('generating');
        try {
            const response = await api.post(`/simulation/generate-job?category=${category}`);
            setGeneratedJob(response.data);
            setJobAnalysisStep('review');
            toast.success(`Generated ${response.data.job_title} role!`);
        } catch (error) {
            console.error('Failed to generate job:', error);
            toast.error('Failed to generate job description');
            setJobAnalysisStep('select');
        }
    };

    // Start the Job Analysis simulation
    const startJobAnalysis = async () => {
        if (!generatedJob) return;
        
        setLoadingScenario('job_analysis');
        try {
            const response = await api.post(`/simulation/start-job-analysis?job_id=${generatedJob.id}`);
            setSimulation(response.data);
            setMessages(response.data.messages);
            setScores(response.data.scores);
            setQuestionsAsked(0);
            setJobAnalysisStep('simulation');
            setAiState('speaking');
            
            // Auto-play TTS for initial message
            if (autoTTS && response.data.messages.length > 0) {
                const lastMsg = response.data.messages[response.data.messages.length - 1];
                if (lastMsg.role === 'assistant') {
                    setTimeout(() => {
                        setMessages(prev => prev.map((msg, idx) => 
                            idx === prev.length - 1 && msg.role === 'assistant' 
                                ? { ...msg, autoPlay: true }
                                : msg
                        ));
                    }, 500);
                }
            }
            
            setTimeout(() => setAiState('idle'), 2000);
            toast.success('Job Analysis simulation started!');
        } catch (error) {
            console.error('Failed to start job analysis:', error);
            toast.error('Failed to start job analysis simulation');
        } finally {
            setLoadingScenario(null);
        }
    };

    // Reset job analysis to select a new job
    const resetJobAnalysis = () => {
        setJobAnalysisStep('select');
        setSelectedJobCategory(null);
        setGeneratedJob(null);
        setQuestionsAsked(0);
    };

    // Start the Communication Simulator
    const startCommunication = async (topicId) => {
        setSelectedCommTopic(topicId);
        setLoadingScenario('communication');
        try {
            const response = await api.post(`/simulation/start-communication?topic_id=${topicId}`);
            setSimulation(response.data);
            setMessages(response.data.messages);
            setScores(response.data.scores);
            setExchanges(0);
            setCommStep('simulation');
            setAiState('speaking');
            
            // Auto-play TTS for initial message
            if (autoTTS && response.data.messages.length > 0) {
                const lastMsg = response.data.messages[response.data.messages.length - 1];
                if (lastMsg.role === 'assistant') {
                    setTimeout(() => {
                        setMessages(prev => prev.map((msg, idx) => 
                            idx === prev.length - 1 && msg.role === 'assistant' 
                                ? { ...msg, autoPlay: true }
                                : msg
                        ));
                    }, 500);
                }
            }
            
            setTimeout(() => setAiState('idle'), 2000);
            toast.success('Communication practice started!');
        } catch (error) {
            console.error('Failed to start communication:', error);
            toast.error('Failed to start communication practice');
        } finally {
            setLoadingScenario(null);
        }
    };

    // Reset communication simulator
    const resetCommunication = () => {
        setCommStep('select');
        setSelectedCommTopic(null);
        setExchanges(0);
        setRealTimeTip(null);
    };

    const startSimulation = async (scenarioType, variantId = null) => {
        // If job analysis, show the category selection instead
        if (scenarioType === 'job_analysis') {
            setSelectedScenario(scenarioTypes.find(s => s.id === 'job_analysis'));
            setJobAnalysisStep('select');
            return;
        }
        
        // If communication, show the topic selection
        if (scenarioType === 'communication') {
            setSelectedScenario(scenarioTypes.find(s => s.id === 'communication'));
            setCommStep('select');
            return;
        }
        
        setLoadingScenario(scenarioType);
        try {
            // Build request payload - include difficulty if coming from R-PAT
            const payload = {
                scenario_type: scenarioType,
                ...(variantId && { variant_id: variantId })
            };
            
            // If coming from R-PAT with a specific difficulty, override the default
            if (fromRpat && rpatDifficulty) {
                payload.difficulty = rpatDifficulty;
            }
            
            const response = await api.post('/simulation/start', payload);
            setSimulation(response.data);
            setMessages(response.data.messages);
            setScores(response.data.scores);
            setAiState('speaking');
            
            // Auto-play TTS for initial message after a short delay
            // This ensures the component is mounted and ready
            if (autoTTS && response.data.messages.length > 0) {
                const lastMsg = response.data.messages[response.data.messages.length - 1];
                if (lastMsg.role === 'assistant') {
                    setTimeout(() => {
                        setMessages(prev => prev.map((msg, idx) => 
                            idx === prev.length - 1 && msg.role === 'assistant' 
                                ? { ...msg, autoPlay: true }
                                : msg
                        ));
                    }, 500);
                }
            }
            
            setTimeout(() => setAiState('idle'), 2000);
            toast.success('Simulation started!');
        } catch (error) {
            console.error('Failed to start simulation:', error);
            toast.error('Failed to start simulation');
        } finally {
            setLoadingScenario(null);
        }
    };

    const sendMessage = async () => {
        if (!inputMessage.trim() || !simulation) return;

        handleUserStartedAnswering(); // User is submitting - stop countdown
        
        const userMessage = { role: 'user', content: inputMessage, timestamp: new Date().toISOString() };
        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setAiState('thinking');

        try {
            const response = await api.post(`/simulation/${simulation.id}/message`, {
                message: inputMessage
            });
            
            const assistantMessage = { 
                role: 'assistant', 
                content: response.data.response,
                timestamp: new Date().toISOString(),
                autoPlay: autoTTS  // Mark this message for auto-play
            };
            setMessages(prev => [...prev, assistantMessage]);
            setScores(response.data.scores);
            setAiState('speaking');
            
            // Track questions asked for job analysis
            if (response.data.questions_asked) {
                setQuestionsAsked(response.data.questions_asked);
            }
            
            // Track exchanges and real-time tips for communication
            if (response.data.exchanges) {
                setExchanges(response.data.exchanges);
            }
            if (response.data.real_time_tip) {
                setRealTimeTip(response.data.real_time_tip);
            }
            
            // Track exchange status for auto-complete
            if (response.data.exchange_status) {
                setExchangeStatus(response.data.exchange_status);
                
                // Show progress message as toast
                if (response.data.exchange_status.progress_message) {
                    const status = response.data.exchange_status;
                    if (status.should_auto_complete) {
                        toast.info(status.progress_message, { duration: 3000 });
                        // Auto-complete the simulation after a brief delay
                        setTimeout(() => {
                            completeSimulation();
                        }, 2000);
                    } else if (status.remaining_exchanges <= 3) {
                        toast.info(status.progress_message, { 
                            duration: 4000,
                            icon: status.is_near_goal ? '🎯' : '⏱️'
                        });
                    }
                }
            }
            
            setTimeout(() => setAiState('idle'), 2000);
        } catch (error) {
            console.error('Failed to send message:', error);
            toast.error('Failed to send message');
            setAiState('idle');
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            analyserRef.current = audioContextRef.current.createAnalyser();
            const source = audioContextRef.current.createMediaStreamSource(stream);
            source.connect(analyserRef.current);
            analyserRef.current.fftSize = 256;
            
            const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
            const monitorLevel = () => {
                if (analyserRef.current) {
                    analyserRef.current.getByteFrequencyData(dataArray);
                    const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
                    setAudioLevel(average / 255);
                }
                if (isRecording) {
                    requestAnimationFrame(monitorLevel);
                }
            };
            
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                audioChunksRef.current.push(event.data);
            };

            mediaRecorder.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                stream.getTracks().forEach(track => track.stop());
                if (audioContextRef.current) {
                    audioContextRef.current.close();
                }
                // Use ref to avoid stale closure
                await sendVoiceMessage(audioBlob, simulationRef.current, questionStartTimeRef.current);
            };

            mediaRecorder.start();
            setIsRecording(true);
            setAiState('listening');
            monitorLevel();
            handleUserStartedAnswering(); // User started answering - stop countdown
            toast.info('Recording...');
        } catch (error) {
            console.error('Failed to start recording:', error);
            toast.error('Could not access microphone');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            setAudioLevel(0);
        }
    };

    const sendVoiceMessage = async (audioBlob, currentSimulation, startTime) => {
        // Use passed parameters to avoid stale closures
        const sim = currentSimulation || simulationRef.current;
        const qStartTime = startTime || questionStartTimeRef.current;
        
        if (!sim) {
            toast.error('Session expired. Please start a new simulation.');
            return;
        }
        
        // Calculate response time for AI detection
        const responseTime = qStartTime ? Date.now() - qStartTime : null;

        setAiState('thinking');
        setTtsFinished(false);
        const formData = new FormData();
        formData.append('audio', audioBlob, 'recording.webm');

        try {
            const response = await api.post(`/simulation/${sim.id}/voice`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            // Check for AI-generated response patterns
            if (responseTime && response.data.transcript) {
                const aiWarnings = analyzeResponseForAI(response.data.transcript, responseTime);
                if (aiWarnings.length > 0) {
                    setAiDetectionWarning(aiWarnings);
                    // Store metrics for later review
                    setResponseMetrics(prev => [...prev, {
                        timestamp: new Date().toISOString(),
                        responseTimeMs: responseTime,
                        wordCount: response.data.transcript.split(/\s+/).length,
                        warnings: aiWarnings
                    }]);
                }
            }

            const userMessage = { 
                role: 'user', 
                content: response.data.transcript, 
                type: 'voice',
                timestamp: new Date().toISOString(),
                responseTimeMs: responseTime
            };
            const assistantMessage = { 
                role: 'assistant', 
                content: response.data.response,
                timestamp: new Date().toISOString(),
                autoPlay: autoTTS  // Mark this message for auto-play
            };
            
            setMessages(prev => [...prev, userMessage, assistantMessage]);
            setScores(response.data.scores);
            setAiState('speaking');
            
            // Track exchange status for auto-complete (voice messages)
            if (response.data.exchange_status) {
                setExchangeStatus(response.data.exchange_status);
                
                // Show progress message as toast
                if (response.data.exchange_status.progress_message) {
                    const status = response.data.exchange_status;
                    if (status.should_auto_complete) {
                        toast.info(status.progress_message, { duration: 3000 });
                        // Auto-complete the simulation after a brief delay
                        setTimeout(() => {
                            completeSimulation();
                        }, 2000);
                    } else if (status.remaining_exchanges <= 3) {
                        toast.info(status.progress_message, { 
                            duration: 4000,
                            icon: status.is_near_goal ? '🎯' : '⏱️'
                        });
                    }
                }
            }
            
            setTimeout(() => setAiState('idle'), 2000);
        } catch (error) {
            console.error('Failed to process voice:', error);
            toast.error('Failed to process voice');
            setAiState('idle');
        }
    };

    const completeSimulation = async () => {
        if (!simulation) return;

        try {
            const response = await api.post(`/simulation/${simulation.id}/complete`);
            setAssessment(response.data.assessment);
            
            // Update scores from the response (this ensures we have the final calculated scores)
            if (response.data.scores) {
                setScores(response.data.scores);
            }
            
            // Show different message for incomplete vs complete simulations
            if (response.data.is_incomplete) {
                toast.warning('Simulation marked as incomplete. Complete at least 3 exchanges for a full score.', {
                    duration: 5000
                });
            } else {
                toast.success('Simulation completed!');
            }
        } catch (error) {
            console.error('Failed to complete simulation:', error);
            toast.error('Failed to complete simulation');
        }
    };

    const resetSimulation = () => {
        setSimulation(null);
        setMessages([]);
        setScores(null);
        setAssessment(null);
        setAiState('idle');
        setSelectedScenario(null);
        setSelectedVariantId(null);
        // Reset job analysis states
        setJobAnalysisStep('select');
        setSelectedJobCategory(null);
        setGeneratedJob(null);
        setQuestionsAsked(0);
        // Reset communication states
        setCommStep('select');
        setSelectedCommTopic(null);
        setExchanges(0);
        setRealTimeTip(null);
        // Reset anti-cheat states
        setCopyAttempts(0);
        setAiDetectionWarning(null);
        setResponseMetrics([]);
        setTtsFinished(false);
        setQuestionStartTime(null);
        setResponseCountdown(null);
        setIsAnswering(false);
        if (autoRecordTimeoutRef.current) {
            clearTimeout(autoRecordTimeoutRef.current);
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
        }
    };

    // Same scenario again (Quality Task retake)
    const retakeSimulation = () => {
        const { scenario_type: type, variant_id: variantId, topic_id: topicId } = simulation;
        resetSimulation();
        if (type === 'communication' && topicId) {
            setSelectedScenario(scenarioTypes.find(s => s.id === 'communication'));
            startCommunication(topicId);
            return;
        }
        startSimulation(type, variantId);
    };

    const qualityRefKey = !simulation ? null
        : simulation.scenario_type === 'communication' ? `communication:${simulation.topic_id}`
        : simulation.scenario_type === 'job_analysis' ? 'job_analysis'
        : `${simulation.scenario_type}:${simulation.variant_id}`;

    const getCurrentScenario = () => {
        return scenarioTypes.find(s => s.id === simulation?.scenario_type);
    };

    // Get score labels for different simulation types
    const getScoreLabels = () => {
        if (simulation?.scenario_type === 'job_analysis') {
            return [
                { key: 'job_understanding', label: 'Job Understanding', icon: BookOpen, color: 'violet' },
                { key: 'role_clarity', label: 'Role Clarity', icon: Target, color: 'blue' },
                { key: 'requirements_knowledge', label: 'Requirements', icon: ListChecks, color: 'emerald' },
                { key: 'communication', label: 'Communication', icon: MessageSquare, color: 'amber' }
            ];
        }
        if (simulation?.scenario_type === 'communication') {
            return [
                { key: 'clarity', label: 'Clarity', icon: Target, color: 'pink' },
                { key: 'fluency', label: 'Fluency', icon: Zap, color: 'purple' },
                { key: 'structure', label: 'Structure', icon: ListChecks, color: 'blue' },
                { key: 'vocabulary', label: 'Vocabulary', icon: BookOpen, color: 'emerald' },
                { key: 'confidence', label: 'Confidence', icon: Award, color: 'amber' },
                { key: 'empathy', label: 'Empathy', icon: Users, color: 'rose' }
            ];
        }
        return [
            { key: 'communication', label: 'Communication', icon: MessageSquare, color: 'primary' },
            { key: 'confidence', label: 'Confidence', icon: Zap, color: 'secondary' },
            { key: 'accuracy', label: 'Accuracy', icon: CheckCircle, color: 'emerald' },
            { key: 'professionalism', label: 'Professionalism', icon: Award, color: 'yellow' }
        ];
    };

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="simulation-page">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                            <Target className="h-8 w-8 text-secondary" />
                            Recruiter Simulation
                        </h1>
                        <p className="text-muted-foreground mt-1">Practice real recruiting scenarios with AI role-play</p>
                    </div>
                    {simulation && !assessment && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setAutoTTS(!autoTTS)}
                            className="gap-2"
                        >
                            {autoTTS ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                            Auto-speak: {autoTTS ? 'On' : 'Off'}
                        </Button>
                    )}
                </div>

                {!simulation ? (
                    /* Scenario Selection */
                    <div className="space-y-6">
                        {!assignedTask && <ScoreTrendChart api={api} />}

                        <ScenarioSelector
                            api={api}
                            selectedScenario={selectedScenario}
                            setSelectedScenario={selectScenario}
                            selectedVariantId={assignedTask && variantFromUrl ? variantFromUrl : selectedVariantId}
                            setSelectedVariantId={setSelectedVariantId}
                            loadingScenario={loadingScenario}
                            startSimulation={startSimulation}
                            assignedVariantId={assignedTask ? variantFromUrl : null}
                            assignedOnly={!!assignedTask}
                        />
                        
                        {/* Job Analysis Panel - shows when job_analysis is selected */}
                        <JobAnalysisPanel
                            selectedScenario={selectedScenario}
                            jobAnalysisStep={jobAnalysisStep}
                            setSelectedScenario={setSelectedScenario}
                            selectedJobCategory={selectedJobCategory}
                            generatedJob={generatedJob}
                            loadingScenario={loadingScenario}
                            generateJob={generateJob}
                            resetJobAnalysis={resetJobAnalysis}
                            startJobAnalysis={startJobAnalysis}
                            assignedOnly={!!assignedTask}
                        />
                        
                        {/* Communication Panel - shows when communication is selected */}
                        <CommunicationPanel
                            selectedScenario={selectedScenario}
                            commStep={commStep}
                            setSelectedScenario={setSelectedScenario}
                            loadingScenario={loadingScenario}
                            startCommunication={startCommunication}
                            highlightTopicId={commTopicFromUrl}
                            assignedOnly={!!assignedTask}
                        />
                    </div>
                ) : assessment ? (
                    /* Final Assessment */
                    <AssessmentPanel
                        assessment={assessment}
                        scores={scores}
                        hintsUsed={assessment.hints_used || 0}
                        getScoreLabels={getScoreLabels}
                        resetSimulation={resetSimulation}
                        qualityRefKey={qualityRefKey}
                        retakeSimulation={retakeSimulation}
                        simulation={simulation}
                        scenario={getCurrentScenario()}
                    />
                ) : (
                    /* Active Simulation */
                    <ActiveSimulationPanel
                        simulation={simulation}
                        messages={messages}
                        scores={scores}
                        aiState={aiState}
                        setAiState={setAiState}
                        inputMessage={inputMessage}
                        setInputMessage={setInputMessage}
                        isRecording={isRecording}
                        audioLevel={audioLevel}
                        realTimeTip={realTimeTip}
                        setRealTimeTip={setRealTimeTip}
                        aiDetectionWarning={aiDetectionWarning}
                        setAiDetectionWarning={setAiDetectionWarning}
                        responseCountdown={responseCountdown}
                        ttsFinished={ttsFinished}
                        autoRecordEnabled={autoRecordEnabled}
                        setAutoRecordEnabled={setAutoRecordEnabled}
                        questionsAsked={questionsAsked}
                        isAnswering={isAnswering}
                        exchangeStatus={exchangeStatus}
                        exchanges={exchanges}
                        messagesEndRef={messagesEndRef}
                        autoRecordTimeoutRef={autoRecordTimeoutRef}
                        startRecording={startRecording}
                        stopRecording={stopRecording}
                        sendMessage={sendMessage}
                        completeSimulation={completeSimulation}
                        handleTTSComplete={handleTTSComplete}
                        setMessages={setMessages}
                        getCurrentScenario={getCurrentScenario}
                        getScoreLabels={getScoreLabels}
                        api={api}
                    />
                )}
            </div>
        </MainLayout>
    );
};

export default Simulation;
